// The one start dialog: "Give a task" (now) and "New schedule" (later),
// and the edit of a saved schedule. Who does it · A task or a quick
// question · When · What · Name and Report (later only). Rules and words
// in ./startWorkModel.ts; the When, What and Report parts are
// features/schedules/unified/ScheduleFormParts.tsx.
//
// Now: an agent, a subagent or the Team is given a task (one
// Idempotency-Key per open, so a repeated send gets the same task) and the
// task opens; a workflow starts a run and its run view opens. Later: the
// schedule is made ("Schedule made · Open") or saved. A second schedule on
// the same slot for the same target and recipient is refused `conflict`:
// the form asks why both are needed (scrolled to, and focused) and offers
// "Save anyway". Picking another workflow starts its settings afresh. A
// time limit nobody typed starts at the lower of 60 minutes and what the
// worker may run (a subagent's own limit). On a phone the dialog is a
// sheet (Modal).

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Save, Trash2 } from "lucide-react";

import { useCustomerSettings } from "@/api/customerSettings";
import { useCreateSchedule, useDeleteSchedule, useSchedulesUnified, useUnifiedSchedules, useUpdateSchedule, type UnifiedSchedule } from "@/api/schedulesUnified";
import { useGiveTask } from "@/api/tasks";
import { useStartWorkflowRun, useWorkflow, useWorkflows, workflowError, workflowErrorCode } from "@/api/workflows";
import { Button } from "@/components/ui/Button";
import { useConfirm } from "@/components/ui/ConfirmModal";
import type { DropdownOption } from "@/components/ui/Dropdown";
import { InfoTip } from "@/components/ui/InfoTip";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Segmented } from "@/components/ui/Segmented";
import { toast } from "@/components/ui/Toast";
import { ReportFields, WhatFields, WhenFields } from "@/features/schedules/unified/ScheduleFormParts";
import { MAX_NAME, formFromSchedule, formProblems, scheduleBody, schedulePatch, suggestName, type ScheduleForm } from "@/features/schedules/unified/scheduleFormModel";
import { scheduleTitle } from "@/features/schedules/unified/scheduleListModel";
import { Field } from "@/features/settings/ui";
import { onPhone, taskPath, useTasksBase } from "@/features/tasks/paths";
import { giveBody, taskErrorText } from "@/features/tasks/taskModel";
import { useWorkerOptions } from "@/features/tasks/useWorkerOptions";
import { scheduleParams } from "@/features/workflows/editor/scheduleModel";
import { TeamSetupNotice } from "@/features/workflows/jobs/TeamSetupNotice";
import { initialRunForm } from "@/features/workflows/model";
import { newJobPath, useWorkflowsBase, workflowRunPath } from "@/features/workflows/paths";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";

import {
  askerFor,
  fitMinutes,
  defaultFrom,
  defaultWorker,
  dialogTitle,
  fitWhen,
  formFor,
  laterMinutesProblem,
  giveKind,
  missingRequired,
  moreOptionsPath,
  newIdempotencyKey,
  nowProblems,
  onlyDeclared,
  presetForm,
  questionProblem,
  scheduleErrorText,
  teamMissingRefusal,
  showsQuestion,
  storedWho,
  submitLabel,
  typedChanged,
  whenChoices,
  workerKind,
  workerOfSchedule,
  type OriginalWho,
  type StartPreset,
  type StartWhen,
} from "./startWorkModel";
import { WhoField } from "./WhoField";

export interface StartWorkDialogProps {
  customerId: string;
  preset: StartPreset;
  /** A saved schedule to change (no Now then). */
  schedule?: UnifiedSchedule | null;
  /** Later only: an edit, a chain. */
  laterOnly?: boolean;
  /** The operator's ids per agent and channel; else read from the schedules list. */
  recipients?: Record<string, Record<string, string[]>>;
  onClose: () => void;
}

export function StartWorkDialog({ customerId, preset, schedule = null, laterOnly = false, recipients, onClose }: StartWorkDialogProps) {
  const navigate = useNavigate();
  const tasksBase = useTasksBase();
  const workflowsBase = useWorkflowsBase();
  const tasksOn = useCustomerSettings(customerId || null).data?.tasks_enabled === true;
  const { unified, known } = useSchedulesUnified(customerId || null);
  const list = useUnifiedSchedules(customerId || null, { quiet: true }).data;
  const listed = list?.recipients;
  const titleOf = (n: string) => {
    const row = list?.schedules?.find((r) => r.name === n);
    return row ? scheduleTitle(row) : undefined;
  };
  const dir = useAgentDirectory(customerId);
  const standard = dir.agents.filter((a) => a.kind === "standard");
  const firstStandard = standard[0]?.template ?? "";
  const isStandard = (t: string) => standard.some((a) => a.template === t);
  const { options: workers, isSubagent, limitOf } = useWorkerOptions(customerId, true);
  const workflowList = useWorkflows(customerId || null);
  const workflows: DropdownOption<string>[] = (workflowList.data?.workflows ?? []).map((w) => ({ value: w.slug, label: w.name || w.slug }));
  const give = useGiveTask();
  const start = useStartWorkflowRun();
  const create = useCreateSchedule();
  const update = useUpdateSchedule();
  const remove = useDeleteSchedule();
  const confirm = useConfirm();

  const [initial] = useState<ScheduleForm>(() => (schedule ? formFromSchedule(schedule) : presetForm(preset)));
  const [form, setForm] = useState<ScheduleForm>(initial);
  const [workerSel, setWorkerSel] = useState<string | null>(schedule ? workerOfSchedule(schedule) : preset.to ?? null);
  const [fromSel, setFromSel] = useState<string | null>(null);
  const [fromAuto, setFromAuto] = useState<string | null>(null);
  const [whenSel, setWhenSel] = useState<StartWhen>(() => (schedule ? formFromSchedule(schedule).when : preset.when ?? "now"));
  const [named, setNamed] = useState(!!schedule);
  const [minutesTyped, setMinutesTyped] = useState(!!schedule || !!preset.again?.minutes);
  const [tried, setTried] = useState(false);
  const [reason, setReason] = useState("");
  const [badParams, setBadParams] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const reasonRef = useRef<HTMLInputElement>(null);
  const alertRef = useRef<HTMLParagraphElement>(null);
  const set = (patch: Partial<ScheduleForm>) => setForm((f) => ({ ...f, ...patch }));
  const setWhat = (patch: Partial<ScheduleForm>) => {
    if ("minutes" in patch) setMinutesTyped(true);
    set(patch);
  };

  // who does it, and whose chat it reports in ("From"): picked, else what the door and the agents give;
  // From follows a standard agent picked as the worker until From itself is picked
  const worker = workerSel ?? defaultWorker(preset, firstStandard);
  const kind = workerKind(worker, isStandard);
  // the most a task of this worker may run (a subagent's own limit): the default comes down to it
  const cap = kind === "agent" || kind === "subagent" ? limitOf(worker) : null;
  const minutes = fitMinutes(form.minutes, cap, minutesTyped);
  const savedWorker = schedule ? workerOfSchedule(schedule) : "";
  const original: OriginalWho | null = schedule
    ? { worker: savedWorker, from: schedule.owner || defaultFrom(preset, savedWorker, isStandard, firstStandard), who: schedule.who.kind === "subagent" ? "agent" : schedule.who.kind, ref: schedule.who.ref, owner: schedule.owner }
    : null;
  // a question is asked by someone else: a From nobody picked never asks its own worker (a saved row keeps its own)
  const from =
    fromSel ??
    (original && fromAuto === null ? original.from : askerFor(fromAuto ?? defaultFrom(preset, worker, isStandard, firstStandard), worker, form.question, standard.map((a) => a.template)));

  const choices = whenChoices({ unified, tasksOn, editing: !!schedule, laterOnly, worker: kind });
  // until the store's answer is in, the door's When stands
  const when = known || schedule ? fitWhen(whenSel, choices) : whenSel;
  const now = when === "now";
  const stored = storedWho(worker, from, form.report, isStandard, original, form.question);
  const askable = showsQuestion(now, kind, stored, form.question);
  const view = formFor({ ...form, minutes, when: now ? form.when : when }, stored, askable);
  const name = named ? form.name : suggestName(view);
  const full = { ...view, name };

  const slug = kind === "workflow" ? stored.ref : "";
  const spec = useWorkflow(slug ? customerId || null : null, slug || null).data?.spec;
  const workflowName = workflows.find((w) => w.value === slug)?.label ?? null;
  // a run now is given its settings as the form shows them (an untouched on/off setting goes as off, as the card's Run sends it)
  const runParams = spec ? scheduleParams(spec.parameters ?? [], initialRunForm(spec.parameters ?? [], form.params)) : form.params;
  const asked = questionProblem({ now, question: view.question, stored, report: form.report });
  const overCap = now ? null : laterMinutesProblem(full, cap);
  const problems: Partial<Record<string, string>> = now
    ? nowProblems({ kind, brief: form.brief, title: form.title, minutes, question: view.question, missing: spec ? missingRequired(spec.parameters ?? [], runParams) : [], badParams, cap })
    : { ...formProblems(full), ...(overCap ? { minutes: overCap } : {}), ...(asked ? { question: asked } : {}) };
  const shown: Partial<Record<string, string>> = tried ? problems : {};

  const saving = give.isPending || start.isPending || create.isPending || update.isPending;
  const busy = saving || remove.isPending;
  const conflict = !now && workflowErrorCode(create.error ?? update.error) === "conflict";
  const saveError = create.error ?? update.error ?? remove.error;
  const failed = now ? (give.error ? taskErrorText(give.error) : start.error ? workflowError(start.error) : null) : saveError ? scheduleErrorText(saveError, titleOf) : null;
  const agents: DropdownOption<string>[] = standard.map((a) => ({ value: a.template, label: a.name }));
  const moreOptions = now && kind === "team" && !onPhone(tasksBase);

  // a refused duplicate: bring "Why both are needed" and the reason into view, and type there
  useEffect(() => {
    if (!conflict) return;
    alertRef.current?.scrollIntoView({ block: "nearest" });
    reasonRef.current?.focus({ preventScroll: true });
  }, [conflict]);

  function pickWorker(w: string) {
    setWorkerSel(w);
    if (isStandard(w)) setFromAuto(w);
    // another workflow starts from its own settings (the saved row's again when it is the saved one)
    if (w !== worker && (w.startsWith("workflow:") || worker.startsWith("workflow:"))) {
      set({ params: schedule && w === savedWorker ? { ...(schedule.what.params ?? {}) } : {} });
      setBadParams(false);
    }
  }

  function submit(force = false) {
    setTried(true);
    if (Object.keys(problems).length) return;
    if (now && kind === "workflow") {
      const params = spec ? onlyDeclared(runParams, spec.parameters ?? []) : form.params;
      start.mutate({ customerId, slug, params }, { onSuccess: (run) => go(workflowRunPath(workflowsBase, run.run_id)) });
    } else if (now) {
      const body = giveBody({ to: worker, title: form.title, brief: form.brief, kind: view.question ? "question" : "task", minutes, needsBrowser: form.needsBrowser }, giveKind(kind));
      give.mutate({ customerId, body, idempotencyKey: key }, { onSuccess: (task) => go(taskPath(tasksBase, task.task_id)) });
    } else if (!force || reason.trim()) {
      const extra = force ? { reason } : undefined;
      if (schedule) update.mutate({ customerId, id: schedule.id, patch: schedulePatch(full, isSubagent, extra) }, { onSuccess: onClose });
      else create.mutate({ customerId, body: scheduleBody(full, isSubagent, extra) }, { onSuccess: (row) => made(row.id) });
    }
  }

  /** Close, then open what was started (the page under the dialog may move its own URL as it closes). */
  function go(to: string) {
    onClose();
    navigate(to);
  }

  function made(id: string) {
    onClose();
    const at = `${onPhone(tasksBase) ? "/companion/schedules" : "/schedules"}?id=${encodeURIComponent(id)}`;
    toast.success("Schedule made", { action: { label: "Open", onClick: () => navigate(at) } });
  }

  async function del() {
    if (!schedule) return;
    const ok = await confirm({ title: `Delete “${schedule.name}”?`, body: "Nothing more is given or sent on this schedule. What it already gave stays on the Tasks page.", confirmLabel: "Delete", danger: true });
    if (ok) remove.mutate({ customerId, id: schedule.id }, { onSuccess: onClose });
  }

  const Icon = now ? Play : Save;
  return (
    <Modal
      open
      size="lg"
      onClose={() => !busy && onClose()}
      title={dialogTitle({ when, editing: schedule ? scheduleTitle(schedule) : null, workflowName: kind === "workflow" ? workflowName ?? slug : null })}
      dismissible={!busy}
      dirty={typedChanged(initial, form, named)}
      footer={
        <>
          {schedule && (
            <Button variant="danger" className="mr-auto" onClick={() => void del()} loading={remove.isPending} disabled={busy}>
              {!remove.isPending && <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.75} />}
              Delete
            </Button>
          )}
          {moreOptions && (
            <Button variant="ghost" className="mr-auto" disabled={busy} onClick={() => go(moreOptionsPath(newJobPath(workflowsBase), { ...form, minutes }))}>
              More options
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => submit(conflict)} loading={saving} disabled={busy || (conflict && !reason.trim())}>
            {!saving && <Icon aria-hidden className="h-4 w-4" strokeWidth={1.75} />}
            {submitLabel({ when, editing: !!schedule, conflict })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit(conflict);
        }}
      >
        <WhoField value={worker} workers={workers} now={now} nameOf={dir.nameOf} error={shown.ref} onChange={pickWorker} />
        {askable && (
          <div className="flex flex-col gap-1.5">
            <Segmented<"task" | "question">
              ariaLabel="Kind"
              size="sm"
              value={view.question ? "question" : "task"}
              onChange={(k) => set({ question: k === "question" })}
              options={[
                { value: "task", label: "A task" },
                { value: "question", label: "A quick question" },
              ]}
            />
            {shown.question && <p className="text-xs text-danger">{shown.question}</p>}
          </div>
        )}
        {known && <WhenFields form={view} set={set} shown={shown} tried={tried} workflows={workflows} choices={choices} when={when} onWhen={setWhenSel} />}
        <WhatFields customerId={customerId} form={view} set={setWhat} shown={shown} tried={tried} later={!now} maxMinutes={cap} onParamsProblem={setBadParams} />
        {shown.params && <p className="text-xs text-danger">{shown.params}</p>}
        {!now && (
          <Field
            label={
              <span className="inline-flex items-center gap-1.5">
                Name
                <InfoTip text="How you and the agents find this schedule." />
              </span>
            }
            error={shown.name}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                maxLength={MAX_NAME + 20}
                value={name}
                placeholder="morning-brief"
                onChange={(e) => {
                  setNamed(true);
                  set({ name: e.target.value });
                }}
              />
            )}
          </Field>
        )}
        {!now && <ReportFields form={view} set={set} shown={shown} recipients={(recipients ?? listed ?? {})[from] ?? {}} from={from} agents={agents} onFrom={setFromSel} />}
        {conflict && (
          <Field label="Why both are needed">
            {({ id, describedBy }) => <Input ref={reasonRef} id={id} aria-describedby={describedBy} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        )}
        {failed && (
          <p ref={alertRef} role="alert" className="text-sm text-danger [overflow-wrap:anywhere]">
            {failed}
          </p>
        )}
        {/* a Team task refused for the missing team, now or on a schedule: the install beside the refusal */}
        {teamMissingRefusal(now, give.error, saveError) && <TeamSetupNotice customerId={customerId} asked />}
      </form>
    </Modal>
  );
}
